import { useCallback, useEffect, useState } from "react";

import {
  getClaimBenefitDetails,
  type ClaimBenefitApiResponse,
} from "@tm/insurance/api";

import type { ClaimContentType, ClaimDetailFetchParams } from "../types";

export type BenefitFetchState = "loading" | "success" | "error";

type BenefitData = NonNullable<ClaimBenefitApiResponse["data"]>;

interface UseClaimBenefitDetailsArgs {
  bookingId: string;
  partnerBookingId?: string;
  currency: string;
  detailFetchParams?: ClaimDetailFetchParams;
  contentType?: ClaimContentType;
}

interface UseClaimBenefitDetailsResult {
  fetchState: BenefitFetchState;
  data: BenefitData | null;
  errorMessage: string;
  retry: () => void;
}

const GENERIC_ERROR = "Something went wrong. Please try again.";

export function useClaimBenefitDetails({
  bookingId,
  partnerBookingId,
  currency,
  detailFetchParams,
  contentType,
}: UseClaimBenefitDetailsArgs): UseClaimBenefitDetailsResult {
  const [fetchState, setFetchState] = useState<BenefitFetchState>("loading");
  const [data, setData] = useState<BenefitData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const retry = useCallback(async () => {
    if (!bookingId || !detailFetchParams) {
      setErrorMessage(GENERIC_ERROR);
      setFetchState("error");
      return;
    }

    setFetchState("loading");
    setErrorMessage("");

    try {
      const response = await getClaimBenefitDetails({
        bookingId,
        ...(partnerBookingId ? { partnerBookingId } : {}),
        categoryId: detailFetchParams.categoryId,
        nodeId: detailFetchParams.nodeId,
        contentType,
        currency,
      });

      if (response?.data) {
        setData(response.data);
        setFetchState("success");
      } else {
        setErrorMessage(GENERIC_ERROR);
        setFetchState("error");
      }
    } catch {
      setErrorMessage(GENERIC_ERROR);
      setFetchState("error");
    }
  }, [bookingId, partnerBookingId, currency, detailFetchParams, contentType]);

  useEffect(() => {
    retry();
  }, [retry]);

  return { fetchState, data, errorMessage, retry };
}
